import { defineStore } from 'pinia'
import { z } from 'zod'
import { useRuntimeConfig } from '#imports'
import { localStorage } from '../../utils/storage'
import debug from '../../utils/console'

const logger = debug.withTag('store:UserEngagementStore')

export const ENGAGEMENT_WINDOW_DAYS = 7
export const USER_TRACKING_STORAGE_KEY = 'app:userEngagement'

const userEngagementSchema = z.object({
  isEngaged: z.boolean(),
  lastEngagementDate: z
    .string()
    .nullable()
    .transform((v) => (v ? new Date(v) : null)),
  lastEngagementAction: z.string().nullable().default(null),
})

export type UserEngagement = z.infer<typeof userEngagementSchema>

export type EngagementAction =
  | 'search'
  | 'shortcut'
  | 'article'
  | 'tab'
  | 'ad'
  | 'news_load'
  | 'game'

export type UserEngagementStateUpdate = UserEngagement & {
  daysSinceInstall: number | null
  daysSinceLastEngagement: number | null
  installDate: string | null
}

const defaultUserEngagement: UserEngagement = userEngagementSchema.parse({
  isEngaged: false,
  lastEngagementDate: null,
  lastEngagementAction: null,
})

function parseYYMMDD(s: string): Date | null {
  if (!/^\d{6}$/.test(s)) return null
  const yy = parseInt(s.slice(0, 2), 10)
  const mm = parseInt(s.slice(2, 4), 10)
  const dd = parseInt(s.slice(4, 6), 10)
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return null
  return new Date(2000 + yy, mm - 1, dd)
}

function readStoredEngagement(): UserEngagement {
  const raw = localStorage.getItem(USER_TRACKING_STORAGE_KEY)
  if (!raw) return defaultUserEngagement

  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    logger.error('Failed to parse user engagement data from localStorage')
    return defaultUserEngagement
  }

  const validation = userEngagementSchema.safeParse(parsed)

  if (!validation.success) {
    logger.error('Invalid user engagement data:', validation.error.errors)
    return defaultUserEngagement
  }

  return validation.data
}

interface EngagementStoreState extends UserEngagement {
  engagementResetInDays: number
  installDate: string | null
}

export const userEngagementStore = defineStore(USER_TRACKING_STORAGE_KEY, {
  state: (): EngagementStoreState => ({
    ...defaultUserEngagement,
    engagementResetInDays: ENGAGEMENT_WINDOW_DAYS,
    installDate: null,
  }),

  getters: {
    isEnabled(): boolean {
      const enableUserTracking = useRuntimeConfig().public.enableUserTracking
      return Boolean(enableUserTracking)
    },
    isEngagementRecent(): boolean {
      const lastEngagementDate = this.lastEngagementDate
        ? new Date(this.lastEngagementDate)
        : null
      if (!lastEngagementDate) return false
      const windowMs = 1000 * 60 * 60 * 24 * this.engagementResetInDays
      return new Date().getTime() - lastEngagementDate.getTime() <= windowMs
    },
    shouldResetEngagement(): boolean {
      return !this.isEngaged || !this.isEngagementRecent
    },
    daysSinceInstall(): number | null {
      if (!this.installDate) return null
      const install = parseYYMMDD(this.installDate)
      if (!install) return null
      const ms = new Date().getTime() - install.getTime()
      return Math.floor(ms / (1000 * 60 * 60 * 24))
    },
    daysSinceLastEngagement(): number | null {
      if (!this.lastEngagementDate) return null
      const ms =
        new Date().getTime() - new Date(this.lastEngagementDate).getTime()
      return Math.floor(ms / (1000 * 60 * 60 * 24))
    },
  },

  actions: {
    async loadUserEngagement() {
      if (!this.isEnabled) return

      const params = new URL(location.href).searchParams
      const ignoreEngagement = params.get('ignoreUserEngagement') === 'true'
      const isEngagedOverride = params.get('isEngaged')

      const { getTypeTag } = await import('../../composables/analytics/helper')

      const typeTag = await getTypeTag()
      const installDate = typeTag.slice(-6)

      const baseEngagement = ignoreEngagement
        ? defaultUserEngagement
        : readStoredEngagement()

      this.$patch({ ...baseEngagement, installDate })

      if (!ignoreEngagement && this.shouldResetEngagement) {
        this.resetUserEngagement()
      }

      if (isEngagedOverride) {
        this.$patch({ isEngaged: isEngagedOverride === 'true' })
      }
    },

    engage(action: EngagementAction): void {
      if (!this.isEnabled) return

      const updatedState = {
        ...this.$state,
        isEngaged: true,
        lastEngagementDate: new Date().toISOString(),
        lastEngagementAction: action,
      }

      const validation = userEngagementSchema.safeParse(updatedState)

      if (!validation.success) {
        logger.error('Invalid user engagement entry', validation.error.errors)
        return
      }

      this.$patch(validation.data)
    },

    resetUserEngagement(): void {
      if (!this.isEnabled) return
      this.$patch(defaultUserEngagement)
    },
  },
})
